import React from 'react';
import { useClient } from '../contexts/ClientContext';

interface MobileBottomNavProps {
    currentPage: string;
    onNavigate: (page: string) => void;
    unreadMessagesCount?: number;
}

export const MobileBottomNav: React.FC<MobileBottomNavProps> = ({
    currentPage,
    onNavigate,
    unreadMessagesCount = 0
}) => {
    const { client, openAuthModal } = useClient();

    // Gestion du clic sur le profil : vers le profil si connecté, sinon vers la création de compte
    const handleProfileClick = () => {
        if (client) {
            onNavigate('profile');
        } else {
            openAuthModal('register');
        }
    };

    // Définition dynamique des boutons
    const navItems = [
        {
            id: 'home',
            label: 'Accueil',
            onClick: () => onNavigate('home'),
            isActive: currentPage === 'home' || !currentPage || currentPage === 'accueil',
            icon: (active: boolean) => (
                <svg className={`w-5 h-5 ${active ? 'text-amber-300 drop-shadow-[0_0_8px_rgba(251,191,36,0.6)]' : 'text-purple-300/70'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                    <polyline points="9 22 9 12 15 12 15 22"></polyline>
                </svg>
            )
        },
        {
            id: 'ressources',
            label: 'Ressources',
            // Le bouton ressource redirige directement vers les médiums connectés
            onClick: () => onNavigate('mediums'),
            isActive: currentPage === 'mediums' || currentPage === 'nos-mediums',
            icon: (active: boolean) => (
                <svg className={`w-5 h-5 ${active ? 'text-amber-300 drop-shadow-[0_0_8px_rgba(251,191,36,0.6)]' : 'text-purple-300/70'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                </svg>
            )
        },
        {
            id: 'profile',
            label: client ? 'Profil' : 'Compte',
            onClick: handleProfileClick,
            isActive: currentPage === 'profile' || currentPage === 'espace-client' || currentPage === 'compte',
            icon: (active: boolean) => (
                <svg className={`w-5 h-5 ${active ? 'text-amber-300 drop-shadow-[0_0_8px_rgba(251,191,36,0.6)]' : 'text-purple-300/70'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                </svg>
            )
        },
        // LE BOUTON MESSAGE N'APPARAÎT QUE SI LE CLIENT A UN COMPTE / EST CONNECTÉ
        ...(client ? [{
            id: 'messages',
            label: 'Messages',
            badge: unreadMessagesCount,
            onClick: () => onNavigate('messages'),
            isActive: currentPage === 'messages' || currentPage === 'messagerie' || currentPage === 'inbox',
            icon: (active: boolean) => (
                <svg className={`w-5 h-5 ${active ? 'text-amber-300 drop-shadow-[0_0_8px_rgba(251,191,36,0.6)]' : 'text-purple-300/70'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                </svg>
            )
        }] : []),
        {
            id: 'menu',
            label: 'Menu',
            onClick: () => onNavigate('menu'),
            isActive: currentPage === 'menu',
            icon: (active: boolean) => (
                <svg className={`w-5 h-5 ${active ? 'text-amber-300 drop-shadow-[0_0_8px_rgba(251,191,36,0.6)]' : 'text-purple-300/70'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="3" y1="12" x2="21" y2="12"></line>
                    <line x1="3" y1="6" x2="21" y2="6"></line>
                    <line x1="3" y1="18" x2="21" y2="18"></line>
                </svg>
            )
        }
    ];

    return (
        <nav 
            className="fixed bottom-0 left-0 right-0 z-[130] xl:hidden bg-[#0a0518]/95 backdrop-blur-2xl border-t border-purple-900/50 px-2 pt-2 pb-[max(0.6rem,env(safe-area-inset-bottom))] shadow-[0_-8px_30px_rgba(0,0,0,0.85)]"
            aria-label="Navigation mobile principale"
        >
            <div className="flex items-center justify-around max-w-lg mx-auto">
                {navItems.map(item => {
                    const isActive = item.isActive;

                    return (
                        <button
                            key={item.id}
                            onClick={item.onClick}
                            className={`relative flex flex-col items-center justify-center py-1 px-3 rounded-2xl transition-all duration-300 cursor-pointer ${
                                isActive 
                                    ? 'text-amber-300 bg-purple-950/60 shadow-[0_0_15px_rgba(108,43,217,0.35)] scale-105' 
                                    : 'text-purple-300/70 hover:text-white hover:bg-white/5 active:scale-95'
                            }`}
                        >
                            {/* Icon */}
                            <div className="relative">
                                {item.icon(isActive)}
                                
                                {/* Badge count for Messages */}
                                {'badge' in item && item.badge && item.badge > 0 ? (
                                    <span className="absolute -top-1.5 -right-2.5 bg-red-600 text-white font-extrabold text-[9px] w-4 h-4 rounded-full flex items-center justify-center shadow-md animate-pulse">
                                        {item.badge > 9 ? '9+' : item.badge}
                                    </span>
                                ) : null}
                            </div>

                            {/* Label */}
                            <span className={`text-[10px] font-medium tracking-wide mt-1 ${isActive ? 'font-bold text-amber-300' : 'text-purple-300/70'}`}>
                                {item.label}
                            </span>

                            {/* Active dot glow */}
                            {isActive && (
                                <span className="absolute -bottom-1 w-1.5 h-1.5 bg-amber-400 rounded-full shadow-[0_0_6px_rgba(251,191,36,1)] animate-twinkle"></span>
                            )}
                        </button>
                    );
                })}
            </div>
        </nav>
    );
};

export default MobileBottomNav;
